import { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { useQueryClient } from '@tanstack/react-query';
import {
  listOutline,
  mailOutline,
  menuOutline,
  readerOutline,
  settingsOutline,
  volumeHighOutline,
} from 'ionicons/icons';
import { useIsAdministrator } from '@/hooks/useIsAdministrator';
import { useAppQueryParams } from '@/hooks/useAppQueryParams';
import { useSessionTimeout, clearLastActivity } from '@/hooks/useSessionTimeout';
import { NavDrawer, type NavDrawerItem } from './NavDrawer';
import { AccountMenu } from './AccountMenu';
import { AnnouncementToasts } from './AnnouncementToasts';
import { UpdateBanner } from './UpdateBanner';
import { Footer } from './Footer';
import { Icon } from './Icon';
import { FundListStateProvider } from '@/state/FundListState';

const MENU_ID = 'main-menu';

/**
 * From the menuItems array in home.page.ts, including its per-item visibility.
 * Ionic's md-down means "below lg" (under 992px), so the Tailwind equivalent
 * is `hidden lg:block`.
 *
 * The fund list comes first because it's the main page; it replaces the
 * "Back to fund list" link, which forced every trip between two secondary
 * pages to go through home. User profile lives in AccountMenu instead.
 */
const SECONDARY_NAV_ITEMS: (NavDrawerItem & { adminOnly?: boolean })[] = [
  // { title: 'Reports', to: '/reports', icon: statsChartOutline },
  // Icons are the ones the Angular menu used.
  { title: 'Alerts', to: '/alerts', icon: volumeHighOutline },
  { title: 'Disclaimers', to: '/disclaimers', icon: readerOutline },
  { title: 'Contact us', to: '/contact-us', icon: mailOutline, className: 'hidden lg:block' },
  // "Can't access admin from phones" in the original.
  {
    title: 'Admin',
    to: '/admin',
    icon: settingsOutline,
    className: 'hidden lg:block',
    adminOnly: true,
  },
];

const LOGO = '/assets/intraday-header-local-1line-some-margin.png';

export function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAdmin } = useIsAdministrator();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { withParams } = useAppQueryParams();

  useSessionTimeout(true);

  // Stable, because NavDrawer's open/close effect depends on it.
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  // Terms acceptance offers no navigation, since until the user accepts every
  // other route bounces back here. It does still offer Sign out, which the
  // earlier layout didn't on phones, leaving no way out but closing the tab.
  const isTocAcceptance = location.pathname === '/toc-acceptance';
  const navItems: NavDrawerItem[] = [
    // Carries the d and t query parameters so returning keeps the same date
    // and tenant. NavLink matches on pathname, so it still shows as active.
    // New with the drawer-everywhere change, so it had no Angular icon.
    { title: 'Fund list', to: withParams('/home'), icon: listOutline },
    ...SECONDARY_NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin),
  ];

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
    clearLastActivity();
    queryClient.clear();
    navigate('/home', { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <UpdateBanner />

      <header className="sticky top-0 z-30 border-b border-black/10 bg-secondary print:static">
        {/*
          Three columns with equal-width sides, so the logo is truly centred
          whatever sits either side of it. With mx-auto on the logo, a wide
          name on the right pushed it off-centre.
        */}
        <div className="mx-auto grid h-14 max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 lg:h-[70px] lg:px-4">
          <div className="justify-self-start">
            {!isTocAcceptance && (
              <button
                type="button"
                aria-label="Open menu"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                aria-controls={MENU_ID}
                onClick={() => setMenuOpen(true)}
                className="rounded-md p-2 text-slate-900 hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <Icon icon={menuOutline} className="size-6" />
              </button>
            )}
          </div>

          {/* The logo is the other route home, by the usual convention. */}
          <Link to={withParams('/home')} aria-label="Intraday Vision fund list" className="flex items-center">
            <img src={LOGO} alt="Intraday Vision" className="h-9 w-auto lg:h-14" />
          </Link>

          <div className="justify-self-end">
            <AccountMenu onSignOut={handleSignOut} showProfileLink={!isTocAcceptance} />
          </div>
        </div>
      </header>

      {!isTocAcceptance && (
        <NavDrawer id={MENU_ID} open={menuOpen} onClose={closeMenu} items={navItems} />
      )}

      <main className="flex-1">
        <FundListStateProvider>
          <Outlet />
        </FundListStateProvider>
      </main>

      <Footer />
      <AnnouncementToasts />
    </div>
  );
}


